import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import Button from 'reactstrap/lib/Button';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { CorePageParams } from 'site-modules/shared/utils/core-page/params';
import { URL_PATHS } from 'site-modules/shared/constants/appraisal/paths';
import { generateNewFlatUrl, generateSrpLinkMakeModel } from 'site-modules/shared/utils/srp-link-constructor';
import { DockedContainer } from 'site-modules/shared/components/docked-container/docked-container';
import { ShoppingLink } from 'site-modules/shared/components/shopping-link/shopping-link';
import { Link } from 'site-modules/shared/components/link/link';

import './docked-srp-link.scss';

const APPRAISAL_CREATIVE_ID = 'docked_appraisal_button';

export function DockedSrpLink({
  params,
  showElementSelector,
  inventoryTotalNumber,
  creativeId,
  trackingId,
  makeName,
  modelName,
  isCore6743Enabled,
  isCore6912Enabled,
}) {
  const { make, model, isDealerlessOem } = params;

  const onDisplayChange = useCallback(isDisplayed => {
    if (isDisplayed) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_WIDGET_VIEW,
        event_data: {
          action_name: TrackingConstant.ACTION_WIDGET_VIEW,
          action_cause: TrackingConstant.ACTION_CAUSE_SCROLL,
          creative_id: APPRAISAL_CREATIVE_ID,
        },
      });
    }
  }, []);

  if (!isCore6912Enabled && isDealerlessOem && !inventoryTotalNumber) {
    return null;
  }

  const srpLinkProps = isDealerlessOem
    ? {
        to: generateSrpLinkMakeModel(make, model),
        label: isCore6743Enabled ? 'See All for Sale' : 'See Used for Sale',
      }
    : {
        to: generateNewFlatUrl({ make, model }),
        label: isCore6743Enabled ? `See all new ${makeName} ${modelName} for sale` : 'See All for Sale',
      };

  return (
    <DockedContainer
      className={isCore6912Enabled ? 'bg-blue-50 px-1 py-1' : undefined}
      onDisplayChange={isCore6912Enabled ? onDisplayChange : undefined}
      showElementSelector={showElementSelector}
    >
      {isCore6912Enabled ? (
        <Button
          tag={Link}
          to={URL_PATHS.APPRAISAL_LANDING}
          rel="nofollow"
          color="outline-blue-50"
          className="docked-appraisal-link rounded-8 w-100 py-0_5 size-16 bg-white text-transform-none"
          data-tracking-id="appraise_your_car"
          data-tracking-parent={APPRAISAL_CREATIVE_ID}
        >
          Appraise my car
        </Button>
      ) : (
        <ShoppingLink
          data-tracking-id={trackingId}
          creativeId={creativeId}
          className="docked-srp-link px-1 py-0_75 bg-white rounded"
          iconClassName="icon-price-tags2"
          iconStyles={{ height: '40px', width: '40px' }}
          {...srpLinkProps}
        />
      )}
    </DockedContainer>
  );
}

DockedSrpLink.propTypes = {
  params: CorePageParams.isRequired,
  showElementSelector: PropTypes.string.isRequired,
  creativeId: PropTypes.string,
  trackingId: PropTypes.string,
  makeName: PropTypes.string,
  modelName: PropTypes.string,
  inventoryTotalNumber: PropTypes.number,
  isCore6743Enabled: PropTypes.bool,
  isCore6912Enabled: PropTypes.bool,
};

DockedSrpLink.defaultProps = {
  creativeId: null,
  trackingId: null,
  makeName: null,
  modelName: null,
  inventoryTotalNumber: null,
  isCore6743Enabled: false,
  isCore6912Enabled: false,
};
